/**
 * The cradle docking flow (M10 P5a9b1, plan 2026-10-17-m10-p5a, Q9, review F19): an S docks into its own L's
 * `DOCK_CRADLE` with the dock autopilot, against the cradle's bay frame rebuilt from the L's pose every tick.
 *
 * - **Request** (`requestCradle`): refused unless the flown hull fits an S cradle (`cradleAccepts`), the L is within
 *   `cradle.requestRangeM`, and the L holds (`cradleHolding`: speed ≤ `moor.holdSpeedMps`, turn ≤
 *   `cradle.holdTurnDegS`). A granted request sets `holdOrdered`: the L is ordered to hold (Q9; the L's helm reads it,
 *   P5a9b2 wires it).
 * - **Phases** (`CRADLE_PHASE`): `none`, `approach` (the autopilot flying into the cradle), `cradled` (the autopilot
 *   arrived; P5a9b2 detaches the S and pins it to the socket). The L stopping its hold mid-approach aborts the flow
 *   (strict, plan §6 risk: "the hold check is strict"); the autopilot failing (a hull that cannot fit) ends it too.
 * - **The step** (`stepCradleFlow`) rebuilds the frame (`cradleFrame`), takes the cradle pad's world velocity
 *   (the L's velocity plus ω × r to the pad) and runs `stepAutodock` with it as `frameVel`, so the autopilot docks on
 *   velocity relative to the L (F19). It writes only a `ControlInput` (D-013).
 *
 * Not wired into the live game yet (P5a9b2: `space/docking.ts` dock-key pick, `sim.ts` carried-pose hook).
 * `requestCradle`, `stepCradleFlow` and `cancelCradle` run per tick: allocation-free, scratch in the state (D-014).
 * Positions are float64 world metres (D-011).
 */
import { Vec3Scratch, type Quat, type Vec3 } from '../core/math'
import { createAutodockState, resetAutodock, stepAutodockAt, zeroControl, type AutodockConfig, type AutodockDef, type AutodockShip, type AutodockState } from '../docking/autopilot'
import { createBayFrame, type BaySocket } from '../docking/bay'
import { cradleAccepts, cradleFrame, cradleHolding, type CradleConfig } from '../docking/cradle'
import type { MoorConfig } from '../docking/moorConfig'
import type { ShipBand } from '../data/schemas'
import type { ControlInput } from '../flight/flightModel'
import type { BayFrame } from './contracts'

export const CRADLE_PHASE = { none: 0, approach: 1, cradled: 2 } as const
export type CradlePhase = (typeof CRADLE_PHASE)[keyof typeof CRADLE_PHASE]

/** Why a cradle request was refused, or `ok`. */
export type CradleRequest = 'ok' | 'notS' | 'outOfRange' | 'notHolding'

/** What one flow step reports: still flying, arrived in the cradle, the L stopped holding, or the hull cannot dock. */
export type CradleStepResult = 'idle' | 'running' | 'arrived' | 'aborted' | 'failed'

/** The L carrying the cradle: float64 world pose, world velocity (m/s), angular velocity (rad/s), bounds radius (m). */
export interface CradleCarrier {
  pos: Readonly<Vec3>
  rot: Readonly<Quat>
  vel: Readonly<Vec3>
  angVel: Readonly<Vec3>
  /** Bounds the L's whole collider round `pos` (the autopilot's clearance sphere). */
  radius: number
}

export interface CradleFlowState {
  phase: CradlePhase
  /** The L is ordered to hold (Q9) while a request stands. */
  holdOrdered: boolean
  /** The dock autopilot's state for the cradle approach. */
  readonly autodock: AutodockState
  /** The cradle's bay frame of the last step (float64 world), rebuilt each tick. */
  readonly frame: BayFrame
  /** The cradle pad's world velocity of the last step (m/s). */
  readonly frameVel: Vec3
}

export interface CradleTuning {
  moor: Readonly<Pick<MoorConfig, 'holdSpeedMps'>>
  cradle: Readonly<Pick<CradleConfig, 'requestRangeM' | 'holdTurnDegS'>>
}

export function createCradleFlow(): CradleFlowState {
  return {
    phase: CRADLE_PHASE.none,
    holdOrdered: false,
    autodock: createAutodockState(),
    frame: createBayFrame(),
    frameVel: new Vec3Scratch(),
  }
}

/**
 * The dock key near an own L: grants the cradle (phase `approach`, the L ordered to hold, the autopilot reset) or
 * says why not, leaving the flow unchanged. Allocation-free.
 */
export function requestCradle(
  flow: CradleFlowState,
  shipPos: Readonly<Vec3>,
  def: Readonly<{ sizeBand: ShipBand }>,
  l: Readonly<CradleCarrier>,
  tuning: Readonly<CradleTuning>,
): CradleRequest {
  if (!cradleAccepts(def)) return 'notS'
  const dx = shipPos.x - l.pos.x
  const dy = shipPos.y - l.pos.y
  const dz = shipPos.z - l.pos.z
  if (!(dx * dx + dy * dy + dz * dz <= tuning.cradle.requestRangeM * tuning.cradle.requestRangeM)) return 'outOfRange'
  if (!cradleHolding(l, tuning.moor, tuning.cradle)) return 'notHolding'
  flow.phase = CRADLE_PHASE.approach
  flow.holdOrdered = true
  resetAutodock(flow.autodock)
  return 'ok'
}

/** Ends the flow (the pilot's cancel, a launch, or an abort): phase `none`, the hold order lifted. Allocation-free. */
export function cancelCradle(flow: CradleFlowState): void {
  flow.phase = CRADLE_PHASE.none
  flow.holdOrdered = false
}

/**
 * The cradle pad's world velocity for an L moving at `l.vel` and turning at `l.angVel`: v + ω × (pad − pos), into
 * `out`. Allocation-free.
 */
export function cradlePadVelocity(l: Readonly<CradleCarrier>, pad: Readonly<Vec3>, out: Vec3): Vec3 {
  const rx = pad.x - l.pos.x
  const ry = pad.y - l.pos.y
  const rz = pad.z - l.pos.z
  const w = l.angVel
  out.x = l.vel.x + w.y * rz - w.z * ry
  out.y = l.vel.y + w.z * rx - w.x * rz
  out.z = l.vel.z + w.x * ry - w.y * rx
  return out
}

/**
 * One tick of the cradle approach: rebuilds the cradle frame from the L's pose, checks the L still holds, and flies
 * the dock autopilot on velocity relative to the cradle. Writes all six axes of `out` (zeroed unless approaching).
 * `arrived` moves the flow to `cradled` (the hold order stays: the L carries the S); `aborted` and `failed` end the
 * flow. Allocation-free.
 */
export function stepCradleFlow(
  flow: CradleFlowState,
  l: Readonly<CradleCarrier>,
  socket: BaySocket,
  ship: Readonly<AutodockShip>,
  def: Readonly<AutodockDef>,
  dockCfg: Readonly<AutodockConfig>,
  tuning: Readonly<CradleTuning>,
  dt: number,
  out: ControlInput,
): CradleStepResult {
  if (flow.phase !== CRADLE_PHASE.approach) {
    zeroControl(out)
    return 'idle'
  }
  if (!cradleHolding(l, tuning.moor, tuning.cradle)) {
    cancelCradle(flow)
    zeroControl(out)
    return 'aborted'
  }
  const frame = cradleFrame(l.pos, l.rot, socket, flow.frame)
  cradlePadVelocity(l, frame.pad, flow.frameVel)
  // The carrier object, not `l.radius`: a float into the un-inlined step is boxed (DA-1, D-TBD).
  const res = stepAutodockAt(flow.autodock, frame, l, ship, def, dockCfg, dt, out, flow.frameVel)
  if (res === 'arrived') {
    flow.phase = CRADLE_PHASE.cradled
    return 'arrived'
  }
  if (res === 'failed') {
    cancelCradle(flow)
    return 'failed'
  }
  return 'running'
}
